// src/modules/operaciones/inventarios/dashboard/KpiCard.js
//
// Tarjeta de indicador clave (KPI): título, valor grande, subtítulo, icono y tendencia.
//
// USO
//   <KpiCard titulo="Venta total" valor={k.ventaTotal} formato="compacto" color="venta" icono={TrendingUp} />
//   <KpiCard titulo="Merma total neta" valor={k.mermaTotalNeta} formato="compacto"
//            subtitulo={`${fmtPorcentaje(k.mermaVentaPct)} de la venta`} />          // negativo → rojo solo
//   <KpiCard titulo="Pérdida bruta" valor={k.perdidaBruta} formato="moneda" color="rojo" icono={AlertTriangle} />
//   <KpiCard titulo="Merma / Venta" valor={k.mermaVentaPct} formato="porcentaje" decimales={2} />
//   <KpiCard titulo="Artículos con merma" valor={k.articulos.conMerma} formato="numero" color="gris" />
//   <KpiCard titulo="Ventas" valor={x} formato="compacto" tendencia={12.5} />        // ▲ +12,5%
//
// PROPS
//   titulo         texto superior (obligatorio)
//   valor          número (null/undefined/NaN → "-")
//   formato        "moneda" | "compacto" | "porcentaje" | "numero"   (defecto "numero")
//                  "porcentaje" recibe el valor YA en porcentaje (−0,11 → "-0,11%")
//   decimales      decimales del valor (moneda: 0 · porcentaje: 2 · número: automático · compacto: 1)
//   signo          true → antepone "+" a los positivos (moneda y porcentaje)
//   color          "rojo" | "verde" | "azul" | "naranja" | "violeta" | "ambar" | "cian" | "gris"
//                  o nombres semánticos: "perdida" | "sobrante" | "venta" | "conocida" | "desconocida"
//                  | "alerta" | "neutro".
//                  Sin `color`: negativo → rojo, positivo → verde, cero/sin dato → gris oscuro.
//                  Para un valor siempre positivo que no deba ir en verde (una venta), pasa color="azul"
//                  o color="gris".
//   variante       "texto" (defecto: valor coloreado, tarjeta blanca) | "fondo" (tarjeta teñida)
//   icono          componente de lucide-react (TrendingUp) o un elemento <TrendingUp />
//   subtitulo      texto pequeño bajo el valor
//   tendencia      número (variación %, p. ej. 12.5 o -3.2) | "subida" | "bajada" | "igual" |
//                  { valor, direccion, etiqueta }  →  flecha ▲/▼ con su color
//   invertirTendencia  true → subir es malo (p. ej. mermas): ▲ rojo, ▼ verde
//   ayuda          texto que aparece al pasar el mouse sobre el título
//   cargando       true → esqueleto de carga
//   className      clases extra

import React from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { fmtMoneda, fmtCompacto, fmtPorcentaje, fmtNumero } from "../utils/informesComun";

/* ========================================================================== */
/* Colores                                                                    */
/* ========================================================================== */

// Clases completas (no se arman con plantillas para que Tailwind las detecte).
const TEMAS = {
  rojo: { valor: "text-red-600", chip: "bg-red-50 text-red-600", fondo: "bg-red-50 border-red-100", barra: "bg-red-500" },
  verde: { valor: "text-emerald-600", chip: "bg-emerald-50 text-emerald-600", fondo: "bg-emerald-50 border-emerald-100", barra: "bg-emerald-500" },
  azul: { valor: "text-blue-600", chip: "bg-blue-50 text-blue-600", fondo: "bg-blue-50 border-blue-100", barra: "bg-blue-500" },
  naranja: { valor: "text-orange-600", chip: "bg-orange-50 text-orange-600", fondo: "bg-orange-50 border-orange-100", barra: "bg-orange-500" },
  violeta: { valor: "text-violet-600", chip: "bg-violet-50 text-violet-600", fondo: "bg-violet-50 border-violet-100", barra: "bg-violet-500" },
  ambar: { valor: "text-amber-600", chip: "bg-amber-50 text-amber-600", fondo: "bg-amber-50 border-amber-100", barra: "bg-amber-500" },
  cian: { valor: "text-cyan-600", chip: "bg-cyan-50 text-cyan-600", fondo: "bg-cyan-50 border-cyan-100", barra: "bg-cyan-500" },
  gris: { valor: "text-gray-900", chip: "bg-gray-100 text-gray-500", fondo: "bg-gray-50 border-gray-200", barra: "bg-gray-400" },
};

// Mismos significados que COLORES_SEMANTICOS de informesComun.js
const ALIAS_COLOR = {
  perdida: "rojo",
  sobrante: "verde",
  venta: "azul",
  conocida: "naranja",
  desconocida: "violeta",
  alerta: "ambar",
  neutro: "gris",
  entrada: "cian",
  salida: "rojo",
};

function resolverTema(color, numero) {
  if (color) {
    const nombre = String(color).toLowerCase();
    return TEMAS[nombre] || TEMAS[ALIAS_COLOR[nombre]] || TEMAS.gris;
  }
  // Automático por signo
  if (numero === null || numero === 0) return TEMAS.gris;
  return numero < 0 ? TEMAS.rojo : TEMAS.verde;
}

/* ========================================================================== */
/* Formato                                                                    */
/* ========================================================================== */

function aNumeroSeguro(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

function formatear(numero, formato, { decimales, signo }) {
  if (numero === null) return "-";
  switch (formato) {
    case "moneda":
      return fmtMoneda(numero, { decimales: decimales ?? 0, signo });
    case "compacto":
      return fmtCompacto(numero, { decimales: decimales ?? 1 });
    case "porcentaje":
      return fmtPorcentaje(numero, decimales ?? 2, { signo });
    default:
      return fmtNumero(numero, decimales ?? null);
  }
}

/* ========================================================================== */
/* Tendencia                                                                  */
/* ========================================================================== */

// Normaliza la tendencia a { direccion: "sube" | "baja" | "igual", texto, etiqueta }
function normalizarTendencia(t) {
  if (t === null || t === undefined || t === "") return null;

  const desdeDireccion = (d) => {
    const k = String(d).toLowerCase();
    if (["sube", "subida", "up", "arriba", "alza"].includes(k)) return "sube";
    if (["baja", "bajada", "down", "abajo", "caida"].includes(k)) return "baja";
    return "igual";
  };

  if (typeof t === "object") {
    const n = aNumeroSeguro(t.valor);
    const direccion = t.direccion ? desdeDireccion(t.direccion) : n === null || n === 0 ? "igual" : n > 0 ? "sube" : "baja";
    return {
      direccion,
      texto: n === null ? "" : fmtPorcentaje(n, 1, { signo: true }),
      etiqueta: t.etiqueta || "",
    };
  }
  if (typeof t === "number") {
    if (!Number.isFinite(t)) return null;
    return { direccion: t === 0 ? "igual" : t > 0 ? "sube" : "baja", texto: fmtPorcentaje(t, 1, { signo: true }), etiqueta: "" };
  }
  return { direccion: desdeDireccion(t), texto: "", etiqueta: "" };
}

function Tendencia({ datos, invertir }) {
  const { direccion, texto, etiqueta } = datos;
  const Flecha = direccion === "sube" ? ArrowUpRight : direccion === "baja" ? ArrowDownRight : Minus;

  // Lo "bueno" es subir, salvo que se invierta (mermas)
  let clase = "text-gray-500 bg-gray-100";
  if (direccion !== "igual") {
    const bueno = invertir ? direccion === "baja" : direccion === "sube";
    clase = bueno ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50";
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold ${clase}`}>
        <Flecha className="w-3.5 h-3.5" aria-hidden="true" />
        {texto}
      </span>
      {etiqueta && <span className="text-gray-500">{etiqueta}</span>}
    </span>
  );
}

/* ========================================================================== */
/* Icono                                                                      */
/* ========================================================================== */

function renderIcono(icono, clase) {
  if (!icono) return null;
  if (React.isValidElement(icono)) return icono;
  const Icono = icono; // componente (lucide-react, forwardRef, función)
  return <Icono className={clase} aria-hidden="true" />;
}

/* ========================================================================== */
/* Componente                                                                 */
/* ========================================================================== */

export function KpiCard({
  titulo,
  valor,
  formato = "numero",
  decimales,
  signo = false,
  color = null,
  variante = "texto",
  icono = null,
  subtitulo = null,
  tendencia = null,
  invertirTendencia = false,
  ayuda = null,
  cargando = false,
  className = "",
}) {
  if (cargando) {
    return (
      <div className={`bg-white border border-gray-200 rounded-xl shadow-sm p-4 animate-pulse ${className}`} aria-busy="true">
        <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
        <div className="h-8 w-32 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-20 bg-gray-100 rounded" />
      </div>
    );
  }

  const numero = aNumeroSeguro(valor);
  const tema = resolverTema(color, numero);
  const texto = formatear(numero, formato, { decimales, signo });
  // En formato compacto, el valor completo se ve al pasar el mouse
  const textoCompleto = formato === "compacto" && numero !== null ? fmtMoneda(numero) : texto;
  const tend = normalizarTendencia(tendencia);

  const estiloTarjeta = variante === "fondo" ? tema.fondo : "bg-white border-gray-200";

  return (
    <article
      className={`relative overflow-hidden border rounded-xl shadow-sm p-4 flex flex-col gap-1 ${estiloTarjeta} ${className}`}
      aria-label={`${titulo}: ${textoCompleto}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3
          className="text-xs font-medium uppercase tracking-wide text-gray-500 leading-snug"
          title={ayuda || undefined}
        >
          {titulo}
        </h3>
        {icono && (
          <span className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg ${tema.chip}`}>
            {renderIcono(icono, "w-4 h-4")}
          </span>
        )}
      </div>

      <p
        className={`text-2xl sm:text-3xl font-bold tabular-nums leading-tight truncate ${tema.valor}`}
        title={textoCompleto}
      >
        {texto}
      </p>

      {(subtitulo || tend) && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
          {tend && <Tendencia datos={tend} invertir={invertirTendencia} />}
          {subtitulo && <span className="text-xs text-gray-500 leading-snug">{subtitulo}</span>}
        </div>
      )}
    </article>
  );
}

export default KpiCard;